import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { CalendarIcon, Loader2, Send, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  { id: "polyurethane_blockwork", label: "Polyurethane Blockwork" },
  { id: "render_expansion_joints", label: "Render Expansion Joints" },
  { id: "fire_rated_caulking", label: "Fire Rated Caulking & Certification" },
  { id: "internal_silicone", label: "Internal Silicone Works" },
  { id: "bathroom_caulking", label: "Bathroom Caulking" },
  { id: "kitchen_caulking", label: "Kitchen Caulking" },
  { id: "structural_control_joints", label: "Structural Control Joints" },
  { id: "other", label: "Other" },
];

export const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    project_type: "",
    services: [],
    project_description: "",
    project_location: "",
    estimated_start_date: null,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.project_type || formData.services.length === 0 || !formData.project_description || !formData.project_location) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        estimated_start_date: formData.estimated_start_date 
          ? format(formData.estimated_start_date, "yyyy-MM-dd") 
          : null,
      };
      
      await axios.post(`${API}/quotes`, payload);
      setSubmitted(true);
      toast.success("Quote request submitted successfully! We'll be in touch soon.");
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Failed to submit quote request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12" data-testid="quote-success">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-[#0F172A] uppercase mb-4">
          Thank You!
        </h3>
        <p className="text-slate-600 max-w-md mx-auto mb-8">
          Your quote request has been received. Our team will review your project 
          details and get back to you within 24-48 business hours.
        </p>
        <Button
          onClick={() => {
            setSubmitted(false);
            setFormData({
              name: "",
              email: "",
              phone: "",
              company: "",
              project_type: "",
              services: [],
              project_description: "",
              project_location: "",
              estimated_start_date: null,
            });
          }}
          className="bg-[#0F172A] hover:bg-[#1E293B] text-white font-heading font-bold uppercase tracking-wide"
          data-testid="submit-another-btn"
        >
          Submit Another Request
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
      {/* Personal Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="name" className="text-[#0F172A] font-medium">
            Full Name *
          </Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="John Smith"
            className="mt-2"
            required
            data-testid="quote-name-input"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-[#0F172A] font-medium">
            Email Address *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="john@example.com"
            className="mt-2"
            required
            data-testid="quote-email-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="phone" className="text-[#0F172A] font-medium">
            Phone Number *
          </Label>
          <Input
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="0412 345 678"
            className="mt-2"
            required
            data-testid="quote-phone-input"
          />
        </div>
        <div>
          <Label htmlFor="company" className="text-[#0F172A] font-medium">
            Company Name (Optional)
          </Label>
          <Input
            id="company"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            placeholder="ABC Construction Pty Ltd"
            className="mt-2"
            data-testid="quote-company-input"
          />
        </div>
      </div>

      {/* Project Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="project_type" className="text-[#0F172A] font-medium">
            Project Type *
          </Label>
          <Select
            value={formData.project_type}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, project_type: value }))}
          >
            <SelectTrigger className="mt-2" data-testid="quote-project-type-select">
              <SelectValue placeholder="Select project type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="commercial">Commercial</SelectItem>
              <SelectItem value="residential">Residential</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="project_location" className="text-[#0F172A] font-medium">
            Project Location *
          </Label>
          <Input
            id="project_location"
            name="project_location"
            value={formData.project_location}
            onChange={handleInputChange}
            placeholder="Surfers Paradise, QLD"
            className="mt-2"
            required
            data-testid="quote-location-input"
          />
        </div>
      </div>

      {/* Services */}
      <div>
        <Label className="text-[#0F172A] font-medium mb-4 block">
          Services Required * (Select all that apply)
        </Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {serviceOptions.map((service) => (
            <label
              key={service.id}
              htmlFor={service.id}
              className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:border-[#F59E0B] transition-colors cursor-pointer"
            >
              <Checkbox
                id={service.id}
                checked={formData.services.includes(service.id)}
                onCheckedChange={(checked) => {
                  setFormData((prev) => ({
                    ...prev,
                    services: checked
                      ? [...prev.services, service.id]
                      : prev.services.filter((id) => id !== service.id),
                  }));
                }}
                data-testid={`quote-service-${service.id}`}
              />
              <span className="text-sm font-medium flex-1">
                {service.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Project Description */}
      <div>
        <Label htmlFor="project_description" className="text-[#0F172A] font-medium">
          Project Description *
        </Label>
        <Textarea
          id="project_description"
          name="project_description"
          value={formData.project_description}
          onChange={handleInputChange}
          placeholder="Please provide details about your project, including scope of work, building type, number of floors/units, and any specific requirements..."
          className="mt-2 min-h-[150px]"
          required
          data-testid="quote-description-input"
        />
      </div>

      {/* Estimated Start Date */}
      <div>
        <Label className="text-[#0F172A] font-medium">
          Estimated Start Date (Optional)
        </Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full mt-2 justify-start text-left font-normal"
              data-testid="quote-date-picker"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formData.estimated_start_date ? (
                format(formData.estimated_start_date, "PPP")
              ) : (
                <span className="text-slate-500">Select a date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={formData.estimated_start_date}
              onSelect={(date) =>
                setFormData((prev) => ({ ...prev, estimated_start_date: date }))
              }
              initialFocus
              disabled={(date) => date < new Date()}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        size="lg"
        disabled={loading}
        className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-[#0F172A] font-heading font-bold uppercase tracking-wide"
        data-testid="quote-submit-btn"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="mr-2 h-5 w-5" />
            Submit Quote Request
          </>
        )}
      </Button>
    </form>
  );
};
